<script lang="ts">
  // Application shell (spec §1.6, §2, §3; components doc §1). Boots the store, switches
  // screens with the docked-compose / split-reading rules, hosts the rail, top bar, sheets,
  // toasts, dialogs, the folder context menu and the App-level keyboard dispatcher.
  import { onMount } from 'svelte';
  import { t } from '$lib/i18n';
  import { inboxPath } from '$lib/mail/folders';
  import { readRail, store } from '$lib/store';
  import type { Screen, ThreadRow } from '$lib/types';
  import { isDesktop as mqDesktop, type MenuAnchor } from '$lib/util/dom';
  import Login from './components/auth/Login.svelte';
  import Compose from './components/compose/Compose.svelte';
  import Contacts from './components/contacts/Contacts.svelte';
  import MessageList from './components/list/MessageList.svelte';
  import OutboxView from './components/outbox/OutboxView.svelte';
  import Reader from './components/reader/Reader.svelte';
  import Settings from './components/settings/Settings.svelte';
  import Dialog from './components/shell/Dialog.svelte';
  import Fab from './components/shell/Fab.svelte';
  import FolderMenu, { type FolderCtx } from './components/shell/FolderMenu.svelte';
  import FoldersSheet from './components/shell/FoldersSheet.svelte';
  import KeysDialog from './components/shell/KeysDialog.svelte';
  import MoveSheet from './components/shell/MoveSheet.svelte';
  import Rail from './components/shell/Rail.svelte';
  import SendUndoToast from './components/shell/SendUndoToast.svelte';
  import SnoozeSheet from './components/shell/SnoozeSheet.svelte';
  import TabBar from './components/shell/TabBar.svelte';
  import Toast from './components/shell/Toast.svelte';
  import TopBar from './components/shell/TopBar.svelte';
  import ViewSheet from './components/shell/ViewSheet.svelte';
  import { createKeyHandler } from './components/shell/keyboard';
  import { applyReadMode, readReadMode, type ReadMode } from './components/shell/readMode';
  import { initialTheme, setTheme, startThemeColorSync, wireSkipLink } from './components/shell/theme';
  import Icon from './components/ui/Icon.svelte';

  /** `tik.composeFull`: `'0'` = docked window, `'1'`/absent = full page (ui.js 1.5-2). */
  const COMPOSE_FULL_KEY = 'tik.composeFull';
  function readComposeFull(): boolean {
    try {
      return localStorage.getItem(COMPOSE_FULL_KEY) !== '0';
    } catch {
      return true;
    }
  }

  let isDesktop = $state(mqDesktop());
  let dark = $state(initialTheme() === 'dark');
  let keysOpen = $state(false);
  let railMini = $state(readRail() === 'mini');
  let dockMin = $state(false);
  let composeFull = $state(readComposeFull());
  let readMode = $state<ReadMode>(readReadMode());
  let folderCtx = $state<FolderCtx | null>(null);
  let folderMenuAnchor = $state<MenuAnchor | null>(null);
  let topBar = $state<TopBar | null>(null);

  const screen = $derived(store.screen);
  /** Docked compose keeps the pane on `composeUnder ?? list(currentFolder)` (spec §3 `Le`). */
  const docked = $derived(isDesktop && screen.name === 'compose');
  const eff = $derived<Screen>(docked ? (store.composeUnder ?? { name: 'list', folder: store.currentFolder }) : screen);
  /** Split reading (`Lt`): desktop and the effective screen is a message. */
  const split = $derived(isDesktop && eff.name === 'read');
  const inboxUnseen = $derived(store.folders.find((f) => f.path === inboxPath(store.folders))?.unseen ?? 0);
  const composeDraft = $derived(screen.name === 'compose' ? (screen.draft ?? {}) : {});

  const handleKey = createKeyHandler({
    keysOpen: () => keysOpen,
    setKeysOpen: (v) => (keysOpen = v),
    folderMenuOpen: () => folderCtx !== null,
    closeFolderMenu,
    dockOpen: () => docked,
    closeDock: () => store.closeCompose(),
    isSplit: () => split,
    searchEl: () => topBar?.searchElement() ?? null,
  });

  function closeFolderMenu() {
    folderCtx = null;
    folderMenuAnchor = null;
  }
  function openFolderMenu(ev: MouseEvent, ctx: FolderCtx) {
    folderCtx = ctx;
    folderMenuAnchor = { x: ev.clientX, y: ev.clientY, above: ev.clientY };
  }
  function onRowContext(ev: MouseEvent, row: ThreadRow) {
    topBar?.openContextMenu(ev, row);
  }
  function toggleReadMode() {
    readMode = readMode === 'full' ? 'split' : 'full';
  }

  // Theme: toggles html.tik-dark and writes tik.theme on every run (spec §3).
  $effect(() => {
    setTheme(dark ? 'dark' : 'light');
  });
  // Title + app badge follow the inbox unseen count.
  $effect(() => {
    store.applyBadge(inboxUnseen);
  });
  // Reading layout (html.tik-read-full + tik.readMode).
  $effect(() => {
    applyReadMode(readMode);
  });
  // The dock's collapsed state never survives leaving compose.
  $effect(() => {
    if (screen.name !== 'compose') dockMin = false;
  });

  onMount(() => {
    void store.boot();
    const stopTheme = startThemeColorSync();
    const stopSkip = wireSkipLink();
    const mq = matchMedia('(min-width: 1100px)');
    const onMq = () => (isDesktop = mq.matches);
    mq.addEventListener?.('change', onMq);
    // The compose stream's full/dock button announces its choice here (and via storage).
    const onComposeFull = (ev: Event) => {
      const d = (ev as CustomEvent<boolean | undefined>).detail;
      composeFull = typeof d === 'boolean' ? d : readComposeFull();
    };
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === COMPOSE_FULL_KEY) composeFull = readComposeFull();
    };
    window.addEventListener('tik:compose-full', onComposeFull);
    window.addEventListener('storage', onStorage);
    return () => {
      stopTheme();
      stopSkip();
      mq.removeEventListener?.('change', onMq);
      window.removeEventListener('tik:compose-full', onComposeFull);
      window.removeEventListener('storage', onStorage);
    };
  });
</script>

<svelte:window onkeydown={handleKey} />

{#if !store.booted}
  <div class="boot"></div>
{:else if screen.name === 'login'}
  <Login />
{:else}
  <div class="shell" class:mini-rail={railMini}>
    <Rail bind:mini={railMini} oncontext={openFolderMenu} />
    <main class="pane" id="tik-main" tabindex="-1">
      {#if eff.name === 'compose'}
        <Compose draft={eff.draft ?? {}} />
      {:else if eff.name === 'settings'}
        <Settings />
      {:else if eff.name === 'contacts'}
        <Contacts />
      {:else}
        <TopBar bind:this={topBar} screen={eff} {split} {readMode} ontogglereadmode={toggleReadMode} />
        <div class="content" class:split>
          {#if eff.name === 'outbox'}
            <OutboxView />
          {:else if eff.name === 'read' && split}
            <div class="pane pane-list"><MessageList oncontext={onRowContext} /></div>
            <div class="pane pane-read"><Reader folder={eff.folder} uid={eff.uid} split={true} /></div>
          {:else if eff.name === 'read'}
            <Reader folder={eff.folder} uid={eff.uid} />
          {:else}
            <MessageList oncontext={onRowContext} />
          {/if}
        </div>
      {/if}
    </main>
  </div>
  {#if eff.name === 'list'}
    <TabBar />
    <Fab />
  {/if}
  {#if store.foldersSheet}<FoldersSheet />{/if}
  <SendUndoToast />
  {#if store.viewSheet}<ViewSheet />{/if}
  {#if store.snoozeTarget}<SnoozeSheet />{/if}
  {#if store.moveSheet}<MoveSheet />{/if}
  <Toast />
  <KeysDialog bind:open={keysOpen} />
  {#if docked}
    <section class="dock" class:min={dockMin} class:tik-full={composeFull} aria-label={t('compose')}>
      <div class="dock-bar">
        <span class="dock-title">{t('compose')}</span>
        <button type="button" class="ghost" aria-expanded={!dockMin} aria-label={dockMin ? t('dockExpand') : t('dockMin')} onclick={() => (dockMin = !dockMin)}>
          <span class="dock-chev" class:up={dockMin}><Icon name="chev" size={18} /></span>
        </button>
      </div>
      <div class="dock-body"><Compose draft={composeDraft} docked={true} /></div>
    </section>
  {/if}
  {#if folderCtx}<FolderMenu ctx={folderCtx} anchor={folderMenuAnchor} onclose={closeFolderMenu} />{/if}
{/if}
<Dialog />

<style>
  /* @css App start */
  .boot {
    height: 100%;
    background: var(--surface-page)
  }

  .shell {
    height: 100dvh;
    display: grid;
    grid-template-columns: 240px 1fr;
    min-height: 0
  }

  .pane {
    display: grid;
    grid-template-rows: auto 1fr;
    min-width: 0;
    min-height: 0
  }

  .content {
    min-height: 0;
    overflow: hidden;
    height: 100%
  }

  .content.split {
    display: grid;
    grid-template-columns: minmax(320px, 420px) 1fr;
    min-height: 0
  }

  .content.split .pane {
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column
  }

  .content.split .pane-list {
    border-inline-end: 1px solid var(--border-subtle)
  }

  .content.split .pane-read {
    overflow: auto
  }

  .shell.mini-rail {
    grid-template-columns: 68px 1fr
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ghost:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .dock {
    position: fixed;
    inset-block-end: 0;
    inset-inline-end: var(--space-6);
    inline-size: min(560px, calc(100vw - 2 * var(--space-6)));
    block-size: min(600px, calc(100dvh - 56px));
    z-index: var(--z-layer);
    display: grid;
    grid-template-rows: auto 1fr;
    min-block-size: 0;
    border: 1px solid var(--border-default);
    border-end-start-radius: 0;
    border-end-end-radius: 0;
    border-start-start-radius: var(--radius-lg);
    border-start-end-radius: var(--radius-lg);
    background: var(--surface-card);
    box-shadow: var(--shadow-xl);
    overflow: hidden
  }

  .dock.min {
    block-size: auto
  }

  .dock.min .dock-body {
    display: none
  }

  .dock-bar {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: 0 var(--space-2) 0 var(--space-4);
    min-block-size: 44px;
    background: var(--surface-sunken);
    border-block-end: 1px solid var(--border-subtle)
  }

  .dock-title {
    flex: 1;
    min-inline-size: 0;
    color: var(--text-strong);
    font-weight: var(--weight-medium);
    font-size: var(--text-sm);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
  }

  .dock-chev {
    display: grid;
    place-items: center
  }

  .dock-chev.up {
    transform: rotate(180deg)
  }

  .dock-body {
    min-block-size: 0;
    overflow: hidden
  }

  .ghost:active {
    background: var(--mail-pressed)
  }

  @media (max-width:900px) {
  .shell {
      grid-template-columns: 1fr
    }
  }

  @media (max-width:900px) {
  .content {
      padding-bottom: calc(var(--tabbar-h) + var(--space-1) + env(safe-area-inset-bottom))
    }
  }

  @media (max-width:900px) {
  .content:has(:global(.reader)) {
      padding-bottom: 0
    }
  }

  .dock.tik-full:not(.min) {
    inset: 0;
    inline-size: auto;
    block-size: auto;
    max-block-size: none;
    border: none;
    border-radius: 0;
    box-shadow: none
  }

  .dock.tik-full:not(.min) .dock-bar {
    padding-block-start: env(safe-area-inset-top)
  }

  .dock.tik-full:not(.min) .dock-title {
    visibility: hidden
  }

  @media (min-width:901px) {
  :global(html.tik-read-full) .content.split {
      grid-template-columns: minmax(0, 1fr)
    }
  }

  @media (min-width:901px) {
  :global(html.tik-read-full) .content.split .pane-list {
      display: none
    }
  }
  /* @css App end */
</style>
